import ClassPresentation from "../../Components/ClassPresentation";
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiCamera, FiCheckCircle, FiClock, FiLock, FiMapPin, FiShield, FiUser } from "react-icons/fi";
import Swal from "sweetalert2";
import api from "../../services/api";
import { getApiErrorMessage } from "../../services/apiError";
import { showApiError } from "../../services/showApiError";
import CepAddressLookup from "../../Components/CepAddressLookup";
import FieldLabel from "../../Components/FieldLabel";
import ReferencePhotoCropModal from "../CadAlunoInt/ReferencePhotoCropModal";
import {
  getPasswordStrength,
  isPasswordWithinPolicy,
  PASSWORD_POLICY_MESSAGE,
} from "../../auth/passwordPolicy";
import Logo from "../../Imagens/logoformari_vertical.jpeg";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";

const onlyDigits = (value = "") => value.replace(/\D/g, "");

const formatCpf = (value = "") => {
  const digits = onlyDigits(value).slice(0, 11);

  return digits
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
};

const formatPhone = (value = "") => {
  const digits = onlyDigits(value).slice(0, 11);

  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }

  return digits
    .replace(/^(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
};

const formatInviteExpiration = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "data indisponível";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
};

const getExpiredInvite = (error) => {
  const data = error?.response?.data;

  if (error?.response?.status !== 410 || data?.status !== "expired") {
    return null;
  }

  return {
    expiresAt: data.expiresAt,
    companyName: data.companyName || "a empresa responsável",
    companyEmail: data.companyEmail,
    companyPhone: data.companyPhone,
  };
};

function RegistrationForm({ token }) {

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [expiredInvite, setExpiredInvite] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  const [name, setName] = useState("");
  const [nickname, setNickname] = useState("");
  const [email, setEmail] = useState("");
  const [cpf, setCpf] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [phone, setPhone] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [street, setStreet] = useState("");
  const [number, setNumber] = useState("");
  const [complement, setComplement] = useState("");
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [uf, setUf] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [isFinancialResponsible, setIsFinancialResponsible] = useState(false);
  const [isBillingAddress, setIsBillingAddress] = useState(true);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [cropModal, setCropModal] = useState({
    isOpen: false,
    imageSrc: null,
    fileName: "",
    mimeType: "",
  });
  const [pendingRegistration, setPendingRegistration] = useState(null);
  const submittedPayload = useRef(null);
  const submissionInFlight = useRef(false);
  const requiresApproval = invite?.requiresApproval !== false;

  useEffect(() => {
    validarConvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validarConvite = async () => {
    setLoading(true);
    setInvite(null);
    setExpiredInvite(null);
    setInviteError(null);

    try {
      const resp = await api.get(`/students/invites/${token}`);
      setInvite(resp.data);
    } catch (error) {
      const expired = getExpiredInvite(error);

      if (expired) {
        setExpiredInvite(expired);
      } else {
        setInviteError("Não foi possível validar este link de cadastro. Confira o endereço e tente novamente.");
        showApiError(error, "Não foi possível validar o cadastro");
      }
    } finally {
      setLoading(false);
    }
  };

  const onPhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCropModal({
      isOpen: true,
      imageSrc: URL.createObjectURL(file),
      fileName: file.name,
      mimeType: file.type,
    });
    e.target.value = "";
  };

  const fecharCropModal = () => {
    if (cropModal.imageSrc?.startsWith("blob:")) {
      URL.revokeObjectURL(cropModal.imageSrc);
    }

    setCropModal({
      isOpen: false,
      imageSrc: null,
      fileName: "",
      mimeType: "",
    });
  };

  const confirmarCrop = async (croppedFile, cropError) => {
    if (cropError) {
      await Swal.fire({
        icon: "error",
        title: "Não foi possível ajustar a foto",
        text: "Selecione outra imagem e tente novamente.",
      });
      return;
    }

    if (!croppedFile) return;

    if (photoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(photoPreview);
    }

    setPhoto(croppedFile);
    setPhotoPreview(URL.createObjectURL(croppedFile));
    fecharCropModal();
  };

  const resetForm = () => {
    setName("");
    setNickname("");
    setEmail("");
    setCpf("");
    setBirthDate("");
    setPhone("");
    setMobilePhone("");
    setZipCode("");
    setStreet("");
    setNumber("");
    setComplement("");
    setDistrict("");
    setCity("");
    setUf("");
    setRegistrationNumber("");
    setIsFinancialResponsible(false);
    setIsBillingAddress(true);
    setPassword("");
    setPasswordConfirmation("");
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhoto(null);
    setPhotoPreview(null);
  };

  const getErrorMessage = (err) => {
    if (err?.response?.status === 409) {
      return "Não foi possível iniciar um novo cadastro com os dados informados. Se você já enviou seus dados, consulte a instituição para acompanhar seu cadastro.";
    }

    const message = err?.response?.data?.message;

    if (Array.isArray(message)) {
      return message.join("\n");
    }

    return message || getApiErrorMessage(err);
  };

  const aplicarEnderecoPorCep = (address) => {
    setStreet(address.street);
    setDistrict(address.district);
    setCity(address.city);
    setUf(address.uf);
  };

  const transmitRegistration = async () => {
    try {
      const response = await api.post("/students/requests", submittedPayload.current);
      const status = response.data?.registrationStatus;
      if (status !== "REGISTERED" && status !== "PENDING_APPROVAL") {
        setPendingRegistration({ status: "UNCERTAIN" });
        return;
      }
      setPendingRegistration({ status, id: response.data?.id });
      submittedPayload.current = null;
      resetForm();
    } catch (error) {
      const message = error?.response?.data?.message;
      const recovering = error?.response?.status === 503 &&
        typeof message === "string" && message.startsWith("Cadastro efetivado; a referencia esta em recuperacao.");
      if (recovering || !error?.response || error.response.status >= 500) {
        setPendingRegistration({ status: recovering ? "RECOVERING" : "UNCERTAIN" });
      } else if (pendingRegistration) {
        // An earlier request may have committed. Do not start a new registration.
        setPendingRegistration((current) => ({ ...current, retryBlocked: true }));
        Swal.fire({ icon: "error", title: "Não foi possível acompanhar o cadastro",
          text: "Entre em contato com a instituição para verificar o cadastro já enviado." });
      } else {
        submittedPayload.current = null;
        Swal.fire({ icon: "error", title: "Erro", text: getErrorMessage(error) });
      }
    }
  };

  const retryRegistration = async () => {
    if (submissionInFlight.current || !submittedPayload.current) return;
    submissionInFlight.current = true;
    setSubmitting(true);
    try { await transmitRegistration(); }
    finally { submissionInFlight.current = false; setSubmitting(false); }
  };

  const enviarSolicitacao = async () => {
    if (submissionInFlight.current) return;
    if (
      !name.trim() ||
      !email.trim() ||
      !cpf.trim() ||
      !phone.trim() ||
      !birthDate ||
      !zipCode.trim() ||
      !street.trim() ||
      !district.trim() ||
      !city.trim() ||
      !uf.trim()
    ) {
      Swal.fire(
        "Dados incompletos",
        "Preencha nome, CPF, email, telefone, nascimento e endereço para continuar.",
        "warning",
      );
      return;
    }

    if (!password || password !== passwordConfirmation) {
      Swal.fire("Senha invalida", "Informe e confirme a senha do aluno.", "warning");
      return;
    }

    if (!isPasswordWithinPolicy(password)) {
      Swal.fire(
        "Senha invalida",
        PASSWORD_POLICY_MESSAGE,
        "warning",
      );
      return;
    }

    if (!photo) {
      Swal.fire("Envie uma foto", "A foto e obrigatoria", "warning");
      return;
    }

    submissionInFlight.current = true;
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("image", photo);

      const uploadResp = await api.post("/students/requests/photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const photoTempToken = uploadResp.data?.photoTempToken;
      if (!photoTempToken) {
        throw new Error("Não foi possível preparar a foto para o cadastro. Tente selecionar a imagem novamente.");
      }

      submittedPayload.current = {
        name: name.trim(),
        nickname: nickname || undefined,
        email: email || undefined,
        cpf: onlyDigits(cpf) || undefined,
        phone: onlyDigits(phone) || undefined,
        mobilePhone: onlyDigits(mobilePhone) || undefined,
        birthDate: birthDate || undefined,
        zipCode: zipCode || undefined,
        street: street || undefined,
        number: number || undefined,
        complement: complement || undefined,
        district: district || undefined,
        city: city || undefined,
        uf: uf || undefined,
        registrationNumber: registrationNumber || undefined,
        isFinancialResponsible,
        isBillingAddress,
        classRole: "none",
        password,
        passwordConfirmation,
        token,
        photoTempToken,
      };
      await transmitRegistration();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: getErrorMessage(err),
      });
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="ext-loading" role="status">Carregando convite...</div>;
  }

  if (expiredInvite) {
    return (
      <div className="TelaLoginCentralizada cad-aluno-ext-page">
        <div className="LoginWrapper">
          <section className="login-container ext-container p-4 shadow text-center" aria-labelledby="expired-invite-title">
            <img src={Logo} alt="Formari" className="ext-logo mb-4" />
            <h1 id="expired-invite-title" className="h4">Este link de cadastro expirou</h1>
            <p className="text-muted">
              O período de cadastro encerrou em {formatInviteExpiration(expiredInvite.expiresAt)}.
            </p>
            <p>
              Para abrir um novo período, entre em contato com <strong>{expiredInvite.companyName}</strong>.
            </p>
            {(expiredInvite.companyEmail || expiredInvite.companyPhone) && (
              <div className="d-flex flex-column gap-2 align-items-center">
                {expiredInvite.companyEmail && (
                  <a href={`mailto:${expiredInvite.companyEmail}`}>{expiredInvite.companyEmail}</a>
                )}
                {expiredInvite.companyPhone && (
                  <a href={`tel:${onlyDigits(expiredInvite.companyPhone)}`}>{formatPhone(expiredInvite.companyPhone)}</a>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className="TelaLoginCentralizada cad-aluno-ext-page">
        <div className="LoginWrapper">
          <section className="login-container ext-container p-4 shadow text-center" aria-labelledby="invite-error-title">
            <img src={Logo} alt="Formari" className="ext-logo mb-4" />
            <h1 id="invite-error-title" className="h4">Não foi possível abrir o cadastro</h1>
            <p role="alert" className="text-muted">{inviteError}</p>
            <button type="button" className="btn btn-primary" onClick={validarConvite}>
              Tentar novamente
            </button>
          </section>
        </div>
      </div>
    );
  }

  if (!invite) return null;

  if (pendingRegistration) {
    const { status, retryBlocked } = pendingRegistration;
    const registered = status === "REGISTERED";
    const recovering = status === "RECOVERING";
    const retryable = recovering || status === "UNCERTAIN";
    const title = registered ? "Cadastro concluído"
      : recovering ? "Cadastro efetivado; foto em recuperação"
      : retryable ? "Aguardando confirmação do cadastro"
      : "Cadastro aguardando aprovação";
    return (
      <div className="TelaLoginCentralizada cad-aluno-ext-page">
        <div className="LoginWrapper">
          <section className="login-container ext-container p-4 shadow text-center" aria-labelledby="registration-result-title">
            <img src={Logo} alt="Formari" className="ext-logo mb-4" />
            <h1 id="registration-result-title" className="h4">{title}</h1>
            <p role="status" className="text-muted">
              {registered ? "Seu cadastro e vínculo com a turma foram concluídos. Entre com o e-mail e a senha informados."
                : recovering ? "Seu cadastro já foi efetivado. Estamos concluindo o preparo da foto de referência. Tente acompanhar novamente, sem preencher um novo cadastro."
                : retryable ? "Ainda não foi possível confirmar o resultado do envio. Tente acompanhar novamente usando os mesmos dados."
                : "Recebemos seu pré-cadastro. A instituição irá revisar seus dados antes de liberar o acesso ao portal."}
            </p>
            {pendingRegistration.id && <small className="text-muted">Protocolo: {pendingRegistration.id}</small>}
            {registered && <div className="mt-3"><Link className="btn btn-primary" to="/">Ir para o login</Link></div>}
            {retryable && !retryBlocked && (
              <button type="button" className="btn btn-primary" disabled={submitting} onClick={retryRegistration}>
                {submitting ? "Consultando cadastro..." : "Acompanhar novamente"}
              </button>
            )}
            {retryable && <p className="mt-3">Se precisar sair desta página ou se o problema persistir, entre em contato com a instituição para acompanhar o cadastro enviado.</p>}
          </section>
        </div>
      </div>
    );
  }

  return (
    <main className="cad-aluno-ext-page">
      <div className="ext-shell">
        <aside className="ext-side-panel" aria-label="Contexto do convite">
          <img src={Logo} alt="Formari" className="ext-logo" />

          <div className="ext-side-copy">
            <span className="ext-eyebrow">Convite de cadastro</span>
            <h1>Complete seu cadastro</h1>
            <p>{requiresApproval ? "Revise seus dados e envie a solicitação para análise da instituição." : "Revise seus dados para concluir o cadastro e o vínculo com a turma."}</p>
          </div>

          <div className="ext-invite-card">
            <div><span>Turma</span><ClassPresentation schoolClass={invite} /></div>
            <div className="ext-invite-deadline">
              <FiClock aria-hidden="true" />
              <span>Disponível até {formatInviteExpiration(invite.expiresAt)}</span>
            </div>
          </div>
        </aside>

        <form className="ext-form-panel" onSubmit={(event) => { event.preventDefault(); enviarSolicitacao(); }}>
          <header className="ext-form-header">
            <span className="ext-eyebrow">Seus dados</span>
            <h2>Conte um pouco sobre você</h2>
            <p>Os campos essenciais ajudam a instituição a confirmar seu cadastro com segurança.</p>
          </header>

          <section className="ext-form-section" aria-labelledby="personal-data-title">
            <div className="ext-section-heading">
              <span><FiUser aria-hidden="true" /></span>
              <div><h3 id="personal-data-title">Dados pessoais</h3><p>Identificação e canais de contato.</p></div>
            </div>
            <div className="ext-field-grid">
              <FieldLabel label="Nome completo"><input className="form-control" autoComplete="name" placeholder="Nome completo" value={name} onChange={(e) => setName(e.target.value)} required /></FieldLabel>
              <FieldLabel label="Apelido"><input className="form-control" placeholder="Como prefere ser chamado" value={nickname} onChange={(e) => setNickname(e.target.value)} /></FieldLabel>
              <FieldLabel label="CPF"><input className="form-control" inputMode="numeric" autoComplete="off" placeholder="000.000.000-00" value={cpf} onChange={(e) => setCpf(formatCpf(e.target.value))} required /></FieldLabel>
              <FieldLabel label="Data de nascimento"><input type="date" className="form-control" aria-label="Data de nascimento" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} required /></FieldLabel>
              <FieldLabel label="Telefone"><input className="form-control" inputMode="tel" autoComplete="tel" placeholder="(00) 00000-0000" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} required /></FieldLabel>
              <FieldLabel label="Celular"><input className="form-control" inputMode="tel" placeholder="(00) 00000-0000" value={mobilePhone} onChange={(e) => setMobilePhone(formatPhone(e.target.value))} /></FieldLabel>
            </div>
          </section>

          <section className="ext-form-section" aria-labelledby="address-title">
            <div className="ext-section-heading">
              <span><FiMapPin aria-hidden="true" /></span>
              <div><h3 id="address-title">Endereço</h3><p>Informe o CEP para agilizar o preenchimento.</p></div>
            </div>
            <div className="ext-field-grid">
              <div className="ext-field-wide">
                <CepAddressLookup address={{ zipCode, street, number, complement, district, city, uf }} onZipCodeChange={setZipCode} onApplyAddress={aplicarEnderecoPorCep} disabled={submitting} inputClassName="form-control" />
              </div>
              <FieldLabel label="Logradouro"><input className="form-control" autoComplete="address-line1" placeholder="Rua, avenida ou praça" value={street} onChange={(e) => setStreet(e.target.value)} required /></FieldLabel>
              <FieldLabel label="Número"><input className="form-control" placeholder="Número" value={number} onChange={(e) => setNumber(e.target.value)} /></FieldLabel>
              <FieldLabel label="Complemento"><input className="form-control" autoComplete="address-line2" placeholder="Apartamento, bloco..." value={complement} onChange={(e) => setComplement(e.target.value)} /></FieldLabel>
              <FieldLabel label="Bairro"><input className="form-control" placeholder="Bairro" value={district} onChange={(e) => setDistrict(e.target.value)} required /></FieldLabel>
              <FieldLabel label="Cidade"><input className="form-control" autoComplete="address-level2" placeholder="Cidade" value={city} onChange={(e) => setCity(e.target.value)} required /></FieldLabel>
              <FieldLabel label="UF"><input className="form-control" autoComplete="address-level1" placeholder="UF" maxLength="2" value={uf} onChange={(e) => setUf(e.target.value.toUpperCase())} required /></FieldLabel>
            </div>
          </section>

          <section className="ext-form-section" aria-labelledby="account-title">
            <div className="ext-section-heading">
              <span><FiLock aria-hidden="true" /></span>
              <div><h3 id="account-title">Conta de acesso</h3><p>{requiresApproval ? "Use seu e-mail e crie a senha que será usada após a aprovação." : "Use seu e-mail e crie a senha para acessar o portal após concluir o cadastro."}</p></div>
            </div>
            <div className="ext-field-grid">
              <FieldLabel label="E-mail" className="ext-field-wide"><input type="email" className="form-control" autoComplete="email" placeholder="seuemail@exemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} required /></FieldLabel>
              <FieldLabel label="Senha inicial"><input type="password" className="form-control" autoComplete="new-password" placeholder="Crie uma senha" value={password} onChange={(e) => setPassword(e.target.value)} required /></FieldLabel>
              <FieldLabel label="Confirmar senha"><input type="password" className="form-control" autoComplete="new-password" placeholder="Repita a senha" value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} required /></FieldLabel>
            </div>
            <p className={`ext-password-feedback strength-${getPasswordStrength(password)?.toLowerCase() || "empty"}`} aria-live="polite">
              {getPasswordStrength(password) ? `Força da senha: ${getPasswordStrength(password)}.` : PASSWORD_POLICY_MESSAGE}
            </p>
          </section>

          <section className="ext-form-section ext-photo-card ext-form-photo-card" aria-labelledby="reference-photo-title">
            <div className="ext-photo-heading">
              <FiCamera aria-hidden="true" />
              <div>
                <h2 id="reference-photo-title">Foto de referência</h2>
                <p>Use uma imagem nítida, com o rosto visível e apenas você em destaque.</p>
              </div>
            </div>

            <label className={`ext-photo-picker ${photoPreview ? "has-photo" : ""}`} htmlFor="photoInput">
              {photoPreview ? (
                <img src={photoPreview} alt="Prévia da foto de referência ajustada" />
              ) : (
                <span className="ext-photo-placeholder">
                  <FiCamera aria-hidden="true" />
                  <strong>Selecionar foto</strong>
                  <small>Você poderá ajustar o enquadramento</small>
                </span>
              )}
            </label>
            <input
              id="photoInput"
              className="ext-photo-input"
              type="file"
              accept="image/*"
              aria-label={photoPreview ? "Trocar foto de referência" : "Foto do aluno"}
              onChange={onPhotoChange}
              disabled={submitting}
            />
            {photoPreview && (
              <label className="ext-photo-change" htmlFor="photoInput">
                <FiCamera aria-hidden="true" /> Trocar e reposicionar foto
              </label>
            )}

            <p className="ext-privacy-note">
              <FiShield aria-hidden="true" />
              Sua foto será usada como referência para localizar suas imagens nos eventos autorizados.
            </p>
          </section>

          <section className="ext-form-section" aria-labelledby="academic-title">
            <div className="ext-section-heading">
              <span><FiCheckCircle aria-hidden="true" /></span>
              <div><h3 id="academic-title">Informações do cadastro</h3><p>Complete os últimos dados antes de enviar.</p></div>
            </div>
            <div className="ext-field-grid">
              <FieldLabel label="Matrícula"><input className="form-control" placeholder="Número de matrícula, se houver" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} /></FieldLabel>
            </div>
            <div className="ext-check-grid">
              <label className="ext-check-line">
                <input type="checkbox" checked={isFinancialResponsible} onChange={(e) => setIsFinancialResponsible(e.target.checked)} />
                <span><strong>Sou responsável financeiro</strong><small>Marque se os pagamentos desta matrícula ficam sob sua responsabilidade.</small></span>
              </label>
              <label className="ext-check-line">
                <input type="checkbox" checked={isBillingAddress} onChange={(e) => setIsBillingAddress(e.target.checked)} />
                <span><strong>Usar este endereço para cobrança</strong><small>O endereço informado acima também será considerado para cobrança.</small></span>
              </label>
            </div>
          </section>

          <footer className="ext-form-footer">
            <div><FiShield aria-hidden="true" /><span>{requiresApproval ? "Ao enviar, sua solicitação ficará aguardando a revisão da instituição." : "Após a validação dos dados, seu cadastro será efetivado e vinculado à turma."}</span></div>
            <button className="ext-submit-button" type="submit" disabled={submitting}>{submitting ? "Enviando cadastro..." : "Enviar cadastro"}</button>
          </footer>
        </form>
      </div>

      <ReferencePhotoCropModal
        isOpen={cropModal.isOpen}
        imageSrc={cropModal.imageSrc}
        fileName={cropModal.fileName}
        mimeType={cropModal.mimeType}
        onClose={fecharCropModal}
        onConfirm={confirmarCrop}
        busy={false}
        confirmLabel="Usar foto ajustada"
      />
    </main>
  );
}

export default function CadAlunoExt() {
  const { token } = useParams();
  return <RegistrationForm key={token} token={token} />;
}
